import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Loader2, CheckCircle2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import OwnershipRecordDialog from '../../components/horses/OwnershipRecordDialog';

const OwnershipTab = ({ horse }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const horseId = horse?.id;

  const { data: ownershipData, isLoading: loading } = useQuery({
    queryKey: QK.ownershipRecords({ horse_id: horseId }),
    queryFn: async () => {
      const [ownershipRes, entitiesRes, usersRes] = await Promise.all([
        api.get(`/ownership-records?horse_id=${horseId}`),
        api.get('/crm-entities'),
        api.get('/users'),
      ]);
      // Use horse.tenant_id directly — avoids reading localStorage inside a queryFn
      const tenantId = horseId ? null : null; // will be resolved from horse prop
      let tenantEntry = null;
      if (horse?.tenant_id) {
        try {
          const t = await api.get(`/tenants/${horse.tenant_id}`);
          if (t.data) tenantEntry = { id: t.data.id, name: t.data.name };
        } catch { /* ignore */ }
      }
      const userMap = {};
      (usersRes.data || []).forEach(u => { userMap[u.id] = `${u.first_name} ${u.last_name}`; });
      const entityMap = {};
      (entitiesRes.data || []).forEach(e => {
        entityMap[e.id] = { name: e.system_name || e.name || 'Unknown', type: 'account', account_manager: e.account_manager_id ? (userMap[e.account_manager_id] || '-') : '-' };
      });
      const records = ownershipRes.data || [];
      return { currentOwnership: records.filter(r => r.is_current), historicalOwnership: records.filter(r => !r.is_current), entities: entityMap, tenantEntry };
    },
    enabled: !!horseId,
  });

  const currentOwnership    = ownershipData?.currentOwnership    ?? [];
  const historicalOwnership = ownershipData?.historicalOwnership ?? [];
  const entities            = ownershipData?.entities            ?? {};
  const tenantEntry         = ownershipData?.tenantEntry         ?? null;

  const invalidateOwnership = () => qc.invalidateQueries({ queryKey: QK.ownershipRecords({ horse_id: horseId }) });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const handleRecordCreated = () => {
    setDialogOpen(false);
    setEditingRecord(null);
    invalidateOwnership();
    toast.success('Ownership record saved successfully');
  };

  const handleRecordClick = (record) => {
    setSelectedRecord(record);
    setDetailOpen(true);
  };

  const handleEditRecord = () => {
    setEditingRecord(selectedRecord);
    setDetailOpen(false);
    setDialogOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getEntityName = (record) => {
    if (record.entity_type === 'tenant') return (tenantEntry?.name || 'Consignor') + ' (Consignor)';
    return entities[record.entity_id]?.name || 'Unknown';
  };

  const getAccountManager = (record) => {
    return entities[record.entity_id]?.account_manager || '-';
  };

  const currentTotal = currentOwnership.reduce((sum, r) => sum + r.ownership_percent, 0);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Ownership */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Current Ownership</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Total: {currentTotal}% {currentTotal === 100 ? '✓' : `(${100 - currentTotal}% remaining)`}
              </p>
            </div>
            <Button
              onClick={() => setDialogOpen(true)}
              variant="ghost"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" /> Add
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {currentOwnership.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No current owners</p>
            </div>
          ) : (
            <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="space-y-2" style={{ minWidth: '400px' }}>
                {/* Column Headers */}
                <div className="grid grid-cols-4 gap-4 pb-2 border-b font-semibold text-xs text-muted-foreground uppercase">
                  <div>Owner</div>
                  <div>Ownership %</div>
                  <div>Account Manager</div>
                  <div>Notable</div>
                </div>
                {/* Data Rows */}
                {currentOwnership.map((record) => (
                  <div
                    key={record.id}
                    className="grid grid-cols-4 gap-4 p-3 border-b last:border-b-0 hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => handleRecordClick(record)}
                  >
                    <div className="flex items-center gap-2">
                      {record.is_primary_owner && <CheckCircle2 className="w-4 h-4 text-primary" />}
                      <p className="text-sm font-medium text-primary hover:underline cursor-pointer" onClick={(e) => { e.stopPropagation(); navigate(`/crm/accounts/${record.entity_id}`); }}>{getEntityName(record)}</p>
                    </div>
                    <div><p className="text-sm">{record.ownership_percent}%</p></div>
                    <div><p className="text-sm text-muted-foreground">{getAccountManager(record)}</p></div>
                    <div>{record.is_notable && <Badge variant="outline" className="text-xs">Notable</Badge>}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Historical Ownership */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Historical Ownership</CardTitle>
        </CardHeader>
        <CardContent>
          {historicalOwnership.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No historical ownership records</p>
            </div>
          ) : (
            <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="space-y-2" style={{ minWidth: '460px' }}>
                {/* Column Headers */}
                <div className="grid grid-cols-5 gap-4 pb-2 border-b font-semibold text-xs text-muted-foreground uppercase">
                  <div>Owner</div>
                  <div>Ownership %</div>
                  <div>Date Sold</div>
                  <div>Account Manager</div>
                  <div>Notable</div>
                </div>
                {/* Data Rows */}
                {historicalOwnership.map((record) => (
                  <div
                    key={record.id}
                    className="grid grid-cols-5 gap-4 p-3 border-b last:border-b-0 hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => handleRecordClick(record)}
                  >
                    <div><p className="text-sm font-medium text-primary hover:underline cursor-pointer" onClick={(e) => { e.stopPropagation(); navigate(`/crm/accounts/${record.entity_id}`); }}>{getEntityName(record)}</p></div>
                    <div><p className="text-sm">{record.ownership_percent}%</p></div>
                    <div><p className="text-sm text-muted-foreground">{formatDate(record.date_sold)}</p></div>
                    <div><p className="text-sm text-muted-foreground">{getAccountManager(record)}</p></div>
                    <div>{record.is_notable && <Badge variant="outline" className="text-xs">Notable</Badge>}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <OwnershipRecordDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingRecord(null);
        }}
        onSuccess={handleRecordCreated}
        horse={horse}
        record={editingRecord}
        entities={entities}
        currentTotal={currentTotal}
        tenantEntry={tenantEntry}
      />

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ownership Record Details</DialogTitle>
          </DialogHeader>
          {selectedRecord && (
            <div className="space-y-4 py-4">
              {/* Badges for status */}
              <div className="flex gap-2">
                <Badge variant={selectedRecord.is_current ? 'default' : 'outline'}>
                  {selectedRecord.is_current ? 'Current' : 'Historical'}
                </Badge>
                {selectedRecord.is_primary_owner && (
                  <Badge className="bg-primary hover:bg-primary/90">
                    Primary Owner
                  </Badge>
                )}
                {selectedRecord.is_notable && (
                  <Badge variant="outline">Notable</Badge>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Owner</p>
                  <p className="font-medium">{getEntityName(selectedRecord)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Ownership %</p>
                  <p className="font-medium">{selectedRecord.ownership_percent}%</p>
                </div>
                
                {selectedRecord.date_acquired && (
                  <div>
                    <p className="text-xs text-muted-foreground">Date Acquired</p>
                    <p className="font-medium">{formatDate(selectedRecord.date_acquired)}</p>
                  </div>
                )}
                
                {selectedRecord.date_sold && (
                  <div>
                    <p className="text-xs text-muted-foreground">Date Sold</p>
                    <p className="font-medium">{formatDate(selectedRecord.date_sold)}</p>
                  </div>
                )}
                
                {selectedRecord.purchase_price && (
                  <div>
                    <p className="text-xs text-muted-foreground">Purchase Price</p>
                    <p className="font-medium">${selectedRecord.purchase_price.toLocaleString()}</p>
                  </div>
                )}
                
                {selectedRecord.sale_price && (
                  <div>
                    <p className="text-xs text-muted-foreground">Sale Price</p>
                    <p className="font-medium">${selectedRecord.sale_price.toLocaleString()}</p>
                  </div>
                )}
              </div>
              
              {selectedRecord.is_notable && selectedRecord.notable_reason && (
                <div>
                  <p className="text-xs text-muted-foreground">Notable Reason</p>
                  <p className="font-medium">{selectedRecord.notable_reason}</p>
                </div>
              )}
              
              {selectedRecord.notes && (
                <div>
                  <p className="text-xs text-muted-foreground">Notes</p>
                  <p className="text-sm mt-1">{selectedRecord.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <div className="flex justify-between w-full">
              <Button variant="ghost" size="icon" onClick={handleEditRecord} title="Edit record">
                <Pencil className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={() => setDetailOpen(false)}>
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OwnershipTab;
